import { useEffect, useMemo, useRef, type ReactNode } from 'react'

import { sendAnalyticsEvent } from '@faststore/sdk'
import { useBreakpoint } from 'gatsby-plugin-breakpoints'
import {
  useAuthenticatedUserToken,
  useUserToken,
} from 'src/sdk/analytics/hooks/useUserToken'
import { useViewItemListEventSearchBar } from 'src/sdk/analytics/hooks/useViewItemListEventSearchBar'
import { SearchPreviewEvent } from 'src/sdk/analytics/types'
import { NoResultsIcon } from '../icons/NoResultsIcon'
import { NoResultsLetter } from '../icons/NoResultsLetter'
import { NoResultsList } from '../icons/NoResultsList'
import { NoResultsTag } from '../icons/NoResultsTag'
import type {
  AutocompleteAnalyticsData,
  PromoHit,
  TopicHit,
  VariantHit,
} from './algolia'
import {
  AutocompleteCategoryItem,
  AutocompleteProductItem,
  AutocompleteSearchHistoryItems,
  AutocompleteSuggestionItem,
  AutocompleteTopicItem,
} from './AutocompleteItems'
import { PromoBanner } from './PromoBanner'
import {
  TYPING_BRAND_MAX,
  TYPING_CATEGORY_MAX,
  TYPING_QS_HITS,
  type FacetValue,
  type TypingStateResult,
} from './searchbarQueries'
import {
  buildBrandPagePath,
  buildCategoryPagePath,
  customFormatSearchPath,
  EMPTY_STATE_PET_CATEGORIES,
  formatFacetLabel,
  SHOW_PROMO_IN_NO_RESULTS,
  useAutocompleteAnalytics,
} from './utils'

export const SectionTitle = ({ children }: { children: ReactNode }) => (
  <p className="m-0 text-[11px] font-bold uppercase tracking-[0.06em] text-[#74767c]">
    {children}
  </p>
)

const EmptyCategoryPills = ({
  onSelect,
  sendAutoCompleteAnalyticsEvent,
  isOpen,
}: {
  onSelect?: () => void
  sendAutoCompleteAnalyticsEvent: (eventData: AutocompleteAnalyticsData) => void
  isOpen: boolean
}) => {
  const sentEventRef = useRef(false)

  useEffect(() => {
    if (!isOpen || sentEventRef.current) return

    sendAutoCompleteAnalyticsEvent({
      topic: 'category',
      objectIDs: EMPTY_STATE_PET_CATEGORIES.map((category) => ({
        suggestion_value: category.label,
      })),
      // queryID: '',
      type: 'view',
      state: 'empty',
    })

    sentEventRef.current = true
  }, [isOpen, sendAutoCompleteAnalyticsEvent])

  return (
    <div className="flex flex-col gap-4">
      <SectionTitle>Sfoglia per categoria</SectionTitle>
      <div className="flex flex-wrap gap-2">
        {EMPTY_STATE_PET_CATEGORIES.map(({ label, href }) => {
          const initial =
            label.trim().charAt(0).toLocaleUpperCase('it-IT') || '?'

          return (
            <a
              key={href}
              href={href}
              onClick={() => {
                sendAutoCompleteAnalyticsEvent({
                  topic: 'category',
                  objectIDs: [{ suggestion_value: label }],
                  // queryID: '',
                  type: 'click',
                  state: 'empty',
                })
                onSelect?.()
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-solid border-[#E0E0E0] bg-white py-1 pl-1 pr-2.5 text-black no-underline"
            >
              <span
                aria-hidden="true"
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#ECECEC] text-[11px] font-semibold text-[#555]"
              >
                {initial}
              </span>
              <span className="text-xs font-semibold tracking-[0.02em]">
                {label}
              </span>
            </a>
          )
        })}
      </div>
    </div>
  )
}

const FacetLinks = ({
  title,
  items,
  onSelect,
  isOpen,
  sendAutoCompleteAnalyticsEvent,
}: {
  title: string
  items: FacetValue[]
  isOpen: boolean
  sendAutoCompleteAnalyticsEvent: (eventData: AutocompleteAnalyticsData) => void
  onSelect?: () => void
}) => {
  if (items.length === 0) return null

  return (
    <div className="flex flex-col gap-4">
      <SectionTitle>{title}</SectionTitle>
      {items.map(({ value, queryID }, index) => {
        const objectIDs = items.map((item) => ({
          suggestion_value: formatFacetLabel(item.value),
        }))

        return (
          <AutocompleteCategoryItem
            key={value}
            href={buildCategoryPagePath(value)}
            label={formatFacetLabel(value)}
            queryID={queryID}
            onSelect={onSelect}
            objectIDs={objectIDs}
            index={index}
            isOpen={isOpen}
            sendAutoCompleteAnalyticsEvent={sendAutoCompleteAnalyticsEvent}
          />
        )
      })}
    </div>
  )
}

// Brand pill stile app
const BrandPills = ({
  items,
  onSelect,
  sendAutoCompleteAnalyticsEvent,
  isOpen,
}: {
  items: FacetValue[]
  onSelect: () => void
  sendAutoCompleteAnalyticsEvent: (eventData: AutocompleteAnalyticsData) => void
  isOpen: boolean
}) => {
  const sentEventRef = useRef(false)

  useEffect(() => {
    if (!isOpen || sentEventRef.current) return

    sendAutoCompleteAnalyticsEvent({
      topic: 'brand',
      objectIDs: items.map((item) => ({ suggestion_value: item.value })),
      queryID: items?.[0]?.queryID,
      type: 'view',
      state: 'typing',
    })

    sentEventRef.current = true
  }, [isOpen, sendAutoCompleteAnalyticsEvent])

  if (items.length === 0) return null

  return (
    <div className="flex flex-col gap-4">
      <SectionTitle>Brand suggeriti</SectionTitle>
      <div className="flex flex-wrap gap-2">
        {items.map(({ value }) => {
          const initial =
            value.trim().charAt(0).toLocaleUpperCase('it-IT') || '?'

          return (
            <a
              key={value}
              href={buildBrandPagePath(value)}
              onClick={() => {
                sendAutoCompleteAnalyticsEvent({
                  topic: 'brand',
                  objectIDs: [{ suggestion_value: value }],
                  queryID: items?.[0]?.queryID,
                  type: 'click',
                  state: 'typing',
                })
                onSelect?.()
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-solid border-[#E0E0E0] bg-white py-1 pl-1 pr-2.5 text-black no-underline"
            >
              <span
                aria-hidden="true"
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#ECECEC] text-[11px] font-semibold text-[#555]"
              >
                {initial}
              </span>
              <span className="text-xs font-semibold uppercase tracking-[0.02em]">
                {value}
              </span>
            </a>
          )
        })}
      </div>
    </div>
  )
}

export const emptyTypingState = (query = ''): TypingStateResult => ({
  query,
  products: [],
  productNbHits: 0,
  productQueryID: '',
  renderingContent: {},
  suggestions: [],
  categories: [],
  brands: [],
})

// Colonna sx piena = 3+2+3. Una riga prodotto (img): circa 2 righe testo.
const LEFT_COLUMN_MAX_ITEMS =
  TYPING_QS_HITS + TYPING_CATEGORY_MAX + TYPING_BRAND_MAX
const PRODUCT_ROWS_MIN = 3
const PRODUCT_ROWS_MAX = 4

// Almeno 3 prodotti; 4 se la colonna sx è abbastanza piena da bilanciare l'altezza.
const visibleProductCount = (leftItemCount: number, isMobile: boolean) => {
  if (isMobile) return PRODUCT_ROWS_MIN
  if (leftItemCount === 0) return PRODUCT_ROWS_MAX

  const proportional = Math.ceil(
    (leftItemCount / LEFT_COLUMN_MAX_ITEMS) * PRODUCT_ROWS_MAX
  )

  return Math.min(PRODUCT_ROWS_MAX, Math.max(PRODUCT_ROWS_MIN, proportional))
}

export type AutocompletePanelProps = {
  promoHit: PromoHit | null
  topicHits: TopicHit[]
  typing: TypingStateResult
  isTyping: boolean
  typingReady: boolean
  onSelectItem: () => void
  isOpen: boolean
  searchHistory: string[]
  deleteFromSearchHistory: (term: string) => void
  clearSearchHistory: () => void
}

// UI dropdown searchbar: empty / typing / no-results
export function AutocompletePanelLayout({
  promoHit,
  topicHits,
  typing,
  isTyping,
  typingReady,
  onSelectItem,
  isOpen,
  searchHistory,
  deleteFromSearchHistory,
  clearSearchHistory,
}: AutocompletePanelProps) {
  const userToken = useUserToken()
  const authenticatedUserToken = useAuthenticatedUserToken()
  const { isMobile } = useBreakpoint()
  const hasResults = typing.products.length > 0
  const suggestions = typing.suggestions.slice(0, TYPING_QS_HITS)
  const categories = typing.categories.slice(0, TYPING_CATEGORY_MAX)
  const brands = typing.brands.slice(0, TYPING_BRAND_MAX)
  const leftItemCount = suggestions.length + categories.length + brands.length
  const products = typing.products.slice(
    0,
    visibleProductCount(leftItemCount, isMobile)
  )
  const showAllResults =
    typing.productNbHits > products.length && typing.query.trim().length > 0
  const { sendViewItemListEvent } = useViewItemListEventSearchBar({ products })
  const { sendAutoCompleteAnalyticsEvent } = useAutocompleteAnalytics()
  const hasSearchHistory = searchHistory.length > 0
  const memoizedQuery = useMemo(() => typing.query, [typing.query])

  useEffect(() => {
    if (isOpen && isTyping && products.length > 0) {
      sendViewItemListEvent(memoizedQuery, typing.productNbHits)
    }
  }, [isTyping, memoizedQuery, isOpen, typing.productNbHits])

  useEffect(() => {
    if (isOpen) {
      sendAnalyticsEvent<SearchPreviewEvent>({
        name: 'search_preview_event',
        params: {
          event: 'ga4_event',
          event_name: 'search_bar_preview_open',
          algolia_user_token: userToken,
          ...(authenticatedUserToken && {
            algolia_authenticated_user_token: authenticatedUserToken,
          }),
        },
      })
    }
  }, [isOpen, userToken, authenticatedUserToken])

  // Risultati solo in typing
  // Mobile: QS+cat unite (no titoli) + prodotti + brand pill.
  // Desktop: sx QS/cat titolate + brand pill | dx prodotti.
  if (isTyping && hasResults) {
    const renderSuggestionItems = (showLeadingIcon: boolean) => {
      const objectIDs = suggestions.map((item) => ({
        object_id: item.objectID,
        suggestion_value: item.query,
      }))

      return suggestions.map((item, index) => (
        <AutocompleteSuggestionItem
          key={item.query}
          item={item}
          query={typing.query}
          onSelect={onSelectItem}
          showLeadingIcon={showLeadingIcon}
          objectIDs={objectIDs}
          index={index}
          sendAutoCompleteAnalyticsEvent={sendAutoCompleteAnalyticsEvent}
          isOpen={isOpen}
        />
      ))
    }

    const renderCategoryItems = (showLeadingIcon: boolean) => {
      const objectIDs = categories.map((item) => ({
        suggestion_value: formatFacetLabel(item.value),
      }))

      return categories.map(({ value, queryID }, index) => (
        <AutocompleteCategoryItem
          key={value}
          href={buildCategoryPagePath(value)}
          label={formatFacetLabel(value)}
          queryID={queryID}
          onSelect={onSelectItem}
          showLeadingIcon={showLeadingIcon}
          index={index}
          isOpen={isOpen}
          objectIDs={objectIDs}
          sendAutoCompleteAnalyticsEvent={sendAutoCompleteAnalyticsEvent}
        />
      ))
    }

    const suggestionsListMobile = renderSuggestionItems(true)
    const suggestionsListDesktop = renderSuggestionItems(false)
    const categoriesListMobile = renderCategoryItems(true)

    const suggestionsBlockDesktop =
      suggestions.length > 0 ? (
        <div className="flex flex-col gap-4">
          <SectionTitle>Suggerimenti</SectionTitle>
          <div className="flex flex-col gap-4">{suggestionsListDesktop}</div>
        </div>
      ) : null

    const categoriesBlockDesktop = (
      <FacetLinks
        title="Categorie suggerite"
        items={categories}
        onSelect={onSelectItem}
        isOpen={isOpen}
        sendAutoCompleteAnalyticsEvent={sendAutoCompleteAnalyticsEvent}
      />
    )

    const brandsBlock = (
      <BrandPills
        items={brands}
        onSelect={onSelectItem}
        sendAutoCompleteAnalyticsEvent={sendAutoCompleteAnalyticsEvent}
        isOpen={isOpen}
      />
    )

    const productsBlock = (
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <SectionTitle>Prodotti</SectionTitle>
        {products.map((item: VariantHit, index) => {
          const objectIDs = products.map((product) => product.objectID)

          return (
            <AutocompleteProductItem
              key={item.objectID}
              item={{ ...item, __position: index + 1 } as VariantHit}
              onSelect={onSelectItem}
              query={typing.query}
              objectIDs={objectIDs}
              pageSize={products.length}
              resultsCount={typing.productNbHits}
              isOpen={isOpen}
            />
          )
        })}
        {showAllResults ? (
          <>
            <a
              href={customFormatSearchPath(typing.query)}
              onClick={onSelectItem}
              className="flex w-full items-center justify-center rounded-full bg-black px-4 py-3 text-sm font-semibold text-white no-underline md:hidden"
            >
              Vedi tutti i {typing.productNbHits} risultati
            </a>
            <a
              href={customFormatSearchPath(typing.query)}
              onClick={onSelectItem}
              className="hidden items-center gap-1 text-sm font-bold text-black no-underline md:inline-flex"
            >
              Vedi tutti i {typing.productNbHits} risultati
              <span aria-hidden="true">→</span>
            </a>
          </>
        ) : null}
      </div>
    )

    const mobileSuggestionsAndCategories =
      suggestions.length > 0 || categories.length > 0 ? (
        <div className="flex flex-col gap-4">
          {suggestionsListMobile}
          {categoriesListMobile}
        </div>
      ) : null

    return (
      <>
        {isMobile && (
          <div className="flex flex-col gap-6 md:hidden">
            {mobileSuggestionsAndCategories}
            {productsBlock}
            {brandsBlock}
          </div>
        )}
        {!isMobile && (
          <div className="hidden gap-10 md:flex md:flex-row">
            <div className="flex flex-col gap-6 w-[42%] shrink-0">
              {suggestionsBlockDesktop}
              {categoriesBlockDesktop}
              {brandsBlock}
            </div>
            {productsBlock}
          </div>
        )}
      </>
    )
  }

  // No-results: copy PLP (+ promo opzionale, vedi SHOW_PROMO_IN_NO_RESULTS)
  if (isTyping && typingReady) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center justify-center gap-3 font-fira">
          <NoResultsIcon />
          <h3 className="font-bold text-base break-all text-center">
            Nessun risultato {typing.query && `per «${typing.query}»`}
          </h3>
          <div className="flex flex-col gap-5">
            <p className="text-sm text-[#75777D] text-center">
              Non abbiamo trovato prodotti per questa ricerca, ma niente paura:
              proviamo insieme in un altro modo.
            </p>
            <div className="flex flex-col gap-3">
              <span className="text-[11px] font-bold text-[#75767c]">
                QUALCHE CONSIGLIO
              </span>
              <div className="flex flex-col gap-2">
                <div className="flex gap-3 items-center">
                  <div className="flex items-center justify-center rounded-full bg-[#f4f4f5] w-8 h-8">
                    <NoResultsLetter />
                  </div>
                  <p className="text-sm">
                    Controlla l'ortografia dei termini inseriti
                  </p>
                </div>
                <div className="flex gap-3 items-center">
                  <div className="flex items-center justify-center rounded-full bg-[#f4f4f5] w-8 h-8">
                    <NoResultsList />
                  </div>
                  <p className="text-sm">
                    Prova con termini più generici, es. "cibo cane"
                  </p>
                </div>
                <div className="flex gap-3 items-center">
                  <div className="flex items-center justify-center rounded-full bg-[#f4f4f5] w-8 h-8">
                    <NoResultsTag />
                  </div>
                  <p className="text-sm">Cerca per marca o per categoria</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {SHOW_PROMO_IN_NO_RESULTS && promoHit ? (
          // If a day this will be rendered, we'll need to differentiate the analytics to Algolia between the two <PromoBanner />
          <PromoBanner
            title={promoHit.sottotitolo ?? promoHit.titolo}
            subtitle={promoHit.descrizione ?? ''}
            href={promoHit.url_landing}
            onClick={onSelectItem}
            objectIDs={[
              {
                object_id: promoHit.objectID,
                suggestion_value: promoHit.sottotitolo ?? promoHit.titolo,
              },
            ]}
            sendAutoCompleteAnalyticsEvent={sendAutoCompleteAnalyticsEvent}
            isOpen={isOpen}
          />
        ) : null}
      </div>
    )
  }

  // Empty / cold, oppure typing ancora in loading: mantiene empty (promo + topic)
  return (
    <div className="flex flex-col gap-6">
      {promoHit ? (
        <PromoBanner
          title={promoHit.sottotitolo ?? promoHit.titolo}
          subtitle={promoHit.descrizione ?? ''}
          href={promoHit.url_landing}
          objectIDs={[
            {
              object_id: promoHit.objectID,
              suggestion_value: promoHit.sottotitolo ?? promoHit.titolo,
            },
          ]}
          queryID={promoHit.__queryID}
          sendAutoCompleteAnalyticsEvent={sendAutoCompleteAnalyticsEvent}
          isOpen={isOpen}
        />
      ) : null}
      <div className="flex flex-col gap-4">
        {hasSearchHistory && (
          <AutocompleteSearchHistoryItems
            isOpen={isOpen}
            searchHistory={searchHistory}
            deleteFromSearchHistory={deleteFromSearchHistory}
            clearSearchHistory={clearSearchHistory}
            sendAutoCompleteAnalyticsEvent={sendAutoCompleteAnalyticsEvent}
          />
        )}
        {!hasSearchHistory && topicHits.length > 0 ? (
          <>
            <SectionTitle>Topic stagionali</SectionTitle>
            {topicHits.map((topic, index) => {
              const objectIDs = topicHits.map((topic) => ({
                object_id: topic.objectID,
                suggestion_value: topic.sottotitolo ?? topic.titolo,
              }))

              return (
                <AutocompleteTopicItem
                  key={topic.objectID}
                  topic={topic}
                  index={index}
                  objectIDs={objectIDs}
                  sendAutoCompleteAnalyticsEvent={
                    sendAutoCompleteAnalyticsEvent
                  }
                  isOpen={isOpen}
                />
              )
            })}
          </>
        ) : null}
      </div>
      <EmptyCategoryPills
        onSelect={onSelectItem}
        sendAutoCompleteAnalyticsEvent={sendAutoCompleteAnalyticsEvent}
        isOpen={isOpen}
      />
    </div>
  )
}
